

























import {
computed,
defineComponent,
ref,
useContext,
useFetch,
useRoute,
} from '@nuxtjs/composition-api';
import { SfBreadcrumbs, SfLoader } from '@storefront-ui/vue';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { merge } from 'lodash-es';
import SeoMeta from '~/components/Seo/SeoMeta.vue';
import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import { useApi, useWishlist } from '~/composables';
import { ALLE_RADER_URL, ROOT_BIKE_CATEGORY } from '~/helpers/routes';
import type { ProductDetailsQuery } from '~/modules/GraphQL/types';
import ProductSkeleton from '~/modules/catalog/product/components/ProductSkeleton.vue';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import { getBreadcrumbs } from '~/modules/catalog/product/getters/productGetters';
import getProductPriceAndCustomBySku from '~/modules/catalog/product/queries/getProductPriceAndCustomBySku.gql';
import type { Product } from '~/modules/catalog/product/types';

export default defineComponent({
  name: 'ProductPage',
  components: {
    ProductSkeleton,
    SimpleBike: () => import('~/modules/catalog/product/components/product-types/bike/SimpleBike.vue'),
    ConfigurableBike: () => import('~/modules/catalog/product/components/product-types/bike/ConfigurableBike.vue'),
    SimpleProduct: () => import('~/modules/catalog/product/components/product-types/simple/SimpleProduct.vue'),
    MageWorxGiftCards: () => import('~/modules/catalog/product/components/product-types/gift-card/MageWorxGiftCards.vue'),
    BundleProduct: () => import('~/modules/catalog/product/components/product-types/bundle/BundleProduct.vue'),
    ConfigurableProduct: () => import('~/modules/catalog/product/components/product-types/configurable/ConfigurableProduct.vue'),
    GroupedProduct: () => import('~/modules/catalog/product/components/product-types/grouped/GroupedProduct.vue'),
    InstagramFeed: () => import('~/components/InstagramFeed.vue'),
    MobileStoreBanner: () => import('~/components/MobileStoreBanner.vue'),
    RelatedProducts: () => import('~/modules/catalog/product/components/RelatedProducts.vue'),
    UpsellProducts: () => import('~/modules/catalog/product/components/UpsellProducts.vue'),
    LoadWhenVisible,
    SfBreadcrumbs,
    SfLoader,
    SeoMeta,
  },
  transition: 'fade',
  setup() {
    const route = useRoute();
    const urlKey = computed(() => route.value.params.url_key);

    const { query } = useApi();
    const product = ref<Product | null>(null);
    const { addTags } = useCache();
    const { localePath } = useContext();
    const { getProductDetails, loading } = useProduct();
    const { error: nuxtError } = useContext();
    const { load: loadWishlist } = useWishlist();
    const breadcrumbs = computed(() => {
      const productCategories = product.value?.categories ?? [];
      return getBreadcrumbs(
        product.value,
        Array.isArray(productCategories) ? [...productCategories].pop() : null,
      ).map((breadcrumb) => ({ ...breadcrumb, link: localePath(breadcrumb.link) }));
    });

    // search property necessary for fetching
    // products with a visibility SEARCH - these should be accessible by URL on the detail page
    const getBaseSearchQuery = () => ({
      search: ' ',
      filter: {
        url_key: {
          eq: urlKey.value,
        },
      },
      configurations: Object.entries(route.value.query)
        .filter((config) => config[0] !== 'wishlist')
        .map(
          (config) => config[1],
        ) as string[],
    });

    const isBike = computed(() => (product.value?.categories?.some((category) => category.url_path === ROOT_BIKE_CATEGORY) ?? false));

    const renderer = computed(() => (isBike.value
      // eslint-disable-next-line no-underscore-dangle
      ? (product.value?.__typename === ProductTypeEnum.CONFIGURABLE_PRODUCT ? ProductTypeEnum.CONFIGURABLE_BIKE : ProductTypeEnum.SIMPLE_BIKE)
      // eslint-disable-next-line no-underscore-dangle
      : product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT));

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      const result = await getProductDetails({
        ...searchQuery,
      });

      if (result) {
        product.value = merge({}, product.value, result.items[0] as Product ?? null);
      } else {
        nuxtError({ statusCode: 404, path: localePath(ALLE_RADER_URL) });
      }
    };

    const fetchProductExtendedData = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await query<ProductDetailsQuery>(getProductPriceAndCustomBySku, searchQuery);

      product.value = merge({}, product.value, data.products?.items?.[0] as Product);
    };

    useFetch(async () => {
      await fetchProductBaseData();
      await fetchProductExtendedData();
      await loadWishlist();

      if (Boolean(product?.value?.sku) === false) nuxtError({ statusCode: 404, path: localePath(ALLE_RADER_URL) });

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${urlKey.value}`,
        },
      ];

      const productTags = [{
        prefix: CacheTagPrefix.Product,
        value: product.value.uid,
      }];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      addTags([...tags, ...productTags]);
    });

    return {
      renderer,
      loading,
      breadcrumbs,
      product,
      fetchProduct: fetchProductExtendedData,
    };
  },
  computed: {
    seoMeta() {
      if (!this.product) return {};

      const activeProduct = this.product as Product;
      const {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            meta_title, meta_description, image, name, short_description, description, meta_keyword, custom,
      } = activeProduct;
      const title = meta_title ?? name;
      const productDescription = meta_description ?? short_description?.html ?? description?.html;
      const imageUrl = image?.url;

      return {
        title,
        description: productDescription,
        og_title: title,
        twitter_title: title,
        og_description: productDescription,
        twitter_description: productDescription,
        og_image: imageUrl,
        twitter_image: imageUrl,
        keywords: meta_keyword,
        manufacturer: custom?.manufacturer_name,
      };
    },
  },
});
